// ============================================================================
// Référentiel National Qualité (RNQ) « Qualiopi » — 7 critères, 32 indicateurs.
// Source : guide de lecture du Ministère du Travail, version mars 2024.
//
// Figé en dur (pas en base) : tous les organismes sont évalués sur exactement le
// même texte, et une mise à jour du guide est un déploiement, pas une migration.
// La base ne référence l'indicateur que par son numéro entier (1 à 32).
//
// Un organisme de formation « classique » est soumis à 23 indicateurs ; les
// indicateurs CFA-only (14, 15, 16, 20, 28, 29) et certifiant-only (3, 7, 8) sont
// marqués comme tels — l'UI les masque/grise selon le profil OF.
// ============================================================================

export const CRITERES = [
  { id: 1, titre: 'Information du public',
    libelle: 'Conditions d\'information du public sur les prestations proposées, les délais pour y accéder et les résultats obtenus.' },
  { id: 2, titre: 'Conception & adaptation',
    libelle: 'Identification précise des objectifs des prestations et adaptation de ces prestations aux publics bénéficiaires.' },
  { id: 3, titre: 'Accompagnement des bénéficiaires',
    libelle: 'Adaptation aux publics, modalités d\'accueil, d\'accompagnement, de suivi et d\'évaluation mises en œuvre.' },
  { id: 4, titre: 'Moyens pédagogiques',
    libelle: 'Adéquation des moyens pédagogiques, techniques et d\'encadrement aux prestations mises en œuvre.' },
  { id: 5, titre: 'Personnel & compétences',
    libelle: 'Qualification et développement des connaissances et compétences des personnels chargés des prestations.' },
  { id: 6, titre: 'Environnement professionnel',
    libelle: 'Inscription et investissement du prestataire dans son environnement professionnel.' },
  { id: 7, titre: 'Appréciations & réclamations',
    libelle: 'Recueil et prise en compte des appréciations et des réclamations des parties prenantes.' },
];

// source : origine de la preuve dans l'outil — 'auto' (déjà dans le CRM),
// 'mixte' (partiellement, à compléter), 'manuel' (rien, tout à téléverser).
export const INDICATEURS = [
  // === Critère 1 — Information du public ====================================
  { id: 1, critere: 1, libelle: 'Diffusion d\'une information accessible',
    description: 'Informer le public, en amont, sur les prestations proposées : prérequis, objectifs, durée, modalités, tarifs, méthodes d\'évaluation, accessibilité handicap, contacts.',
    preuves: ['Site web', 'Plaquettes', 'Programmes détaillés', 'Réponses aux demandes d\'information'],
    source: 'mixte', source_hint: 'formations (titre, objectifs, prérequis, durée, tarifs, modalités, accessibilité)' },
  { id: 2, critere: 1, libelle: 'Indicateurs de résultats',
    description: 'Publier les indicateurs de performance : taux de satisfaction, réussite, abandon, insertion professionnelle, mis à jour annuellement.',
    preuves: ['Taux publiés sur le site', 'Méthodologie de calcul', 'Année de référence'],
    source: 'auto', source_hint: 'inscriptions (satisfaction_chaud_note, score_post, abandon)' },
  { id: 3, critere: 1, libelle: 'Obtention des certifications',
    description: 'Pour les certifications RNCP/RS : communiquer le taux d\'obtention, les débouchés, les passerelles, les équivalences.',
    preuves: ['Taux d\'obtention publiés', 'Fiche RNCP', 'Description des débouchés'],
    certifiant_only: true, source: 'manuel' },

  // === Critère 2 — Conception et adaptation des prestations =================
  { id: 4, critere: 2, libelle: 'Analyse du besoin',
    description: 'Analyser le besoin du bénéficiaire en amont (questionnaire de positionnement, entretien préalable, test de niveau).',
    preuves: ['Questionnaires de positionnement', 'Comptes-rendus d\'entretien', 'Tests d\'évaluation'],
    source: 'mixte', source_hint: 'inscriptions (positionnement pré-formation)' },
  { id: 5, critere: 2, libelle: 'Objectifs opérationnels et évaluables',
    description: 'Définir des objectifs pédagogiques opérationnels et mesurables pour chaque prestation.',
    preuves: ['Programme avec objectifs détaillés', 'Critères de mesure'],
    source: 'auto', source_hint: 'formations (objectifs_pedagogiques)' },
  { id: 6, critere: 2, libelle: 'Contenus, méthodes et évaluation',
    description: 'Formaliser le contenu, les méthodes pédagogiques, les supports et les modalités d\'évaluation des acquis.',
    preuves: ['Programme détaillé', 'Liste des supports', 'Modalités d\'évaluation décrites'],
    source: 'auto', source_hint: 'formations (programme_modules, modalites_pedagogiques, evaluations)' },
  { id: 7, critere: 2, libelle: 'Adéquation aux exigences de certification',
    description: 'Pour les certifications : couverture complète du référentiel de compétences de la certification visée.',
    preuves: ['Matrice de couverture programme × référentiel', 'Référentiel cible'],
    certifiant_only: true, source: 'manuel' },
  { id: 8, critere: 2, libelle: 'Positionnement à l\'entrée',
    description: 'Vérifier l\'adéquation entre le projet du bénéficiaire et la certification visée (positionnement, prérequis).',
    preuves: ['Tests/entretiens documentés', 'Dossiers de candidature'],
    certifiant_only: true, source: 'manuel' },

  // === Critère 3 — Accompagnement des bénéficiaires =========================
  { id: 9, critere: 3, libelle: 'Conditions de déroulement',
    description: 'Informer le bénéficiaire des conditions de déroulement : horaires, lieux, règlement intérieur, contacts, modalités techniques (distanciel).',
    preuves: ['Convocations', 'Règlement intérieur', 'Livret d\'accueil', 'Information sur outils distanciel'],
    source: 'auto', source_hint: 'sessions (lieu, format, date_debut/fin, lien_visio)' },
  { id: 10, critere: 3, libelle: 'Adaptation de la prestation',
    description: 'Adapter la prestation au cours de sa réalisation aux besoins du bénéficiaire (rythme, contenu, accompagnement).',
    preuves: ['Suivi individualisé', 'Adaptations documentées', 'Bilans intermédiaires'],
    source: 'manuel' },
  { id: 11, critere: 3, libelle: 'Évaluation des acquis',
    description: 'Évaluer l\'atteinte par les bénéficiaires des objectifs (quiz, mises en situation, examens, projet) en cours et/ou fin de formation.',
    preuves: ['Évaluations remplies', 'Grilles de notation', 'Synthèses pré/post'],
    source: 'auto', source_hint: 'inscriptions (score_pre, score_post, progression)' },
  { id: 12, critere: 3, libelle: 'Engagement et assiduité',
    description: 'S\'assurer de l\'engagement et de l\'assiduité du bénéficiaire (présence, relance, prévention abandon, points d\'étape).',
    preuves: ['Émargements', 'Tableau d\'assiduité', 'Relances documentées'],
    source: 'auto', source_hint: 'inscriptions (presence_validee, abandon) + signatures (émargement)' },
  { id: 13, critere: 3, libelle: 'Coordination des intervenants',
    description: 'Coordonner les intervenants intervenant sur une même prestation (réunions, partage d\'information, cohérence parcours).',
    preuves: ['Comptes-rendus de réunions pédagogiques', 'Conducteurs partagés'],
    source: 'manuel' },
  { id: 14, critere: 3, libelle: 'Exercice de la citoyenneté (CFA)',
    description: 'Permettre l\'exercice de la citoyenneté de l\'apprenti (élection délégués, information sur droits et devoirs).',
    preuves: ['PV élections', 'Supports d\'information'],
    cfa_only: true, source: 'manuel' },
  { id: 15, critere: 3, libelle: 'Droits et devoirs de l\'apprenti (CFA)',
    description: 'Informer l\'apprenti de ses droits et devoirs (rémunération, congés, protection sociale, contrat).',
    preuves: ['Livret d\'apprentissage', 'Convention signée'],
    cfa_only: true, source: 'manuel' },
  { id: 16, critere: 3, libelle: 'Présentation à la certification (CFA)',
    description: 'Accompagner l\'apprenti dans la présentation aux épreuves de certification.',
    preuves: ['Programme de révisions', 'Suivi des candidatures'],
    cfa_only: true, source: 'manuel' },

  // === Critère 4 — Moyens pédagogiques et techniques ========================
  { id: 17, critere: 4, libelle: 'Moyens humains et techniques',
    description: 'Disposer de moyens humains, techniques et d\'encadrement adaptés aux prestations (locaux, équipements, outils numériques, supports).',
    preuves: ['Inventaire équipements', 'Plans de salles', 'Outils LMS', 'Catalogue supports'],
    source: 'manuel' },
  { id: 18, critere: 4, libelle: 'Coordination des moyens',
    description: 'Coordonner les moyens mobilisés (plan de maintenance, mise à jour régulière des supports).',
    preuves: ['Plan de maintenance', 'Historique des MAJ supports'],
    source: 'manuel' },
  { id: 19, critere: 4, libelle: 'Ressources pédagogiques accessibles',
    description: 'Mettre à disposition des bénéficiaires des ressources pédagogiques accessibles (supports, bibliographies, outils en ligne).',
    preuves: ['Liste ressources distribuées', 'Accès LMS / drive'],
    source: 'auto', source_hint: 'session_documents (livret, supports déposés)' },
  { id: 20, critere: 4, libelle: 'Personnel dédié (CFA)',
    description: 'Disposer d\'un personnel dédié (référent handicap, référent mobilité, conseil en évolution).',
    preuves: ['Fiches de poste', 'Désignation référents'],
    cfa_only: true, source: 'manuel' },

  // === Critère 5 — Personnel : qualification & développement ================
  { id: 21, critere: 5, libelle: 'Compétences des intervenants',
    description: 'Déterminer, mobiliser et évaluer les compétences des intervenants (internes & sous-traitants) en lien avec les prestations.',
    preuves: ['Dossier par intervenant : CV, diplômes, expérience', 'Procédure de sélection'],
    source: 'auto', source_hint: 'intervenants + documents_contractuels (CV, diplômes)' },
  { id: 22, critere: 5, libelle: 'Entretien et développement des compétences',
    description: 'Entretenir et développer les compétences des personnels (formation continue, veille, événements professionnels).',
    preuves: ['Plan de développement', 'Actions de DPC tracées', 'Entretiens annuels'],
    source: 'auto', source_hint: 'formateur_actions_dpc (auto-déclaration formateur)' },

  // === Critère 6 — Environnement professionnel ==============================
  { id: 23, critere: 6, libelle: 'Veille légale et réglementaire',
    description: 'Réaliser une veille sur les évolutions légales et réglementaires (formation, qualité, droits) et l\'exploiter.',
    preuves: ['Registre de veille', 'Abonnements / sources', 'Comptes-rendus'],
    source: 'manuel' },
  { id: 24, critere: 6, libelle: 'Veille pédagogique et innovations',
    description: 'Réaliser une veille sur les évolutions pédagogiques et technologiques (méthodes, outils, innovations) et l\'exploiter.',
    preuves: ['Registre de veille pédagogique', 'Articles partagés aux intervenants'],
    source: 'manuel' },
  { id: 25, critere: 6, libelle: 'Veille compétences et métiers',
    description: 'Réaliser une veille sur l\'évolution des compétences, des métiers et des emplois du secteur.',
    preuves: ['Fiches RNCP suivies', 'Rapports de branche', 'Études sectorielles'],
    source: 'manuel' },
  { id: 26, critere: 6, libelle: 'Mobilisation d\'un réseau et accueil PSH',
    description: 'Mobiliser des partenariats (entreprises, financeurs, prescripteurs) et garantir l\'accueil des personnes en situation de handicap (référent handicap).',
    preuves: ['Conventions partenaires', 'Désignation référent handicap', 'Procédure d\'accueil PSH'],
    source: 'mixte', source_hint: 'intervenants.logistique (accessibilité) + organismes' },

  // === Critère 7 — Appréciations et réclamations ============================
  { id: 27, critere: 7, libelle: 'Conformité réglementaire',
    description: 'Respecter les obligations réglementaires (déclaration d\'activité, règlement intérieur, assurance RC, protection des données).',
    preuves: ['NDA', 'Règlement intérieur', 'Attestation RC Pro', 'Registre RGPD'],
    source: 'mixte', source_hint: 'organismes + documents (RI, RC Pro, NDA)' },
  { id: 28, critere: 7, libelle: 'Promotion sociale et professionnelle (CFA)',
    description: 'Pour les CFA : favoriser l\'insertion sociale et professionnelle des apprentis, y compris en situation de handicap.',
    preuves: ['Programmes d\'accompagnement à l\'insertion'],
    cfa_only: true, source: 'manuel' },
  { id: 29, critere: 7, libelle: 'Aides sociales (CFA)',
    description: 'Pour les CFA : faciliter l\'accès aux aides sociales (logement, mobilité, restauration).',
    preuves: ['Partenariats CROUS / aides', 'Information apprentis'],
    cfa_only: true, source: 'manuel' },
  { id: 30, critere: 7, libelle: 'Recueil des appréciations',
    description: 'Recueillir les appréciations des bénéficiaires, financeurs et entreprises sur les prestations délivrées.',
    preuves: ['Questionnaires de satisfaction', 'Enquêtes à froid', 'Retours financeurs/entreprises'],
    source: 'auto', source_hint: 'inscriptions (satisfaction_chaud_note, satisfaction_froid_note, satisfaction_criteres, satisfaction_verbatim)' },
  { id: 31, critere: 7, libelle: 'Traitement des réclamations',
    description: 'Mettre en place un processus formalisé de traitement des réclamations (réception, traitement, délai, suivi).',
    preuves: ['Procédure documentée', 'Registre des réclamations', 'Exemples traités'],
    source: 'mixte', source_hint: 'tickets_support + formateur_incidents' },
  { id: 32, critere: 7, libelle: 'Amélioration continue',
    description: 'Mettre en œuvre des actions d\'amélioration continue à partir des retours collectés (plan d\'amélioration, revue de direction).',
    preuves: ['Plan d\'amélioration', 'Comptes-rendus revue de direction', 'Évolutions de formations'],
    source: 'manuel' },
];

// Statuts d'évaluation — score DÉLIBÉRÉMENT binaire : seul 'conforme' compte,
// « partiellement » vaut zéro comme « non conforme » (une non-conformité mineure
// ne rapporte pas de demi-point).
export const STATUTS = ['conforme', 'partiellement', 'non_conforme', 'non_evalue'];

export const STATUT_LABEL = {
  conforme: 'Conforme',
  partiellement: 'Partiellement conforme',
  non_conforme: 'Non conforme',
  non_evalue: 'Non évalué'
};

// Couleurs MUI (bg / texte) — équivalents des paliers Tailwind d'origine.
export const STATUT_COULEUR = {
  conforme: { bg: '#d1fae5', color: '#047857' },
  partiellement: { bg: '#fef3c7', color: '#b45309' },
  non_conforme: { bg: '#fee2e2', color: '#b91c1c' },
  non_evalue: { bg: '#f3f4f6', color: '#6b7280' }
};

// Badges de source (couleurs pour l'UI).
export const SOURCE_LABEL = { auto: 'Auto', mixte: 'Mixte', manuel: 'Manuel' };
export const SOURCE_COULEUR = {
  auto: { bg: '#dcfce7', color: '#15803d' },
  mixte: { bg: '#dbeafe', color: '#1d4ed8' },
  manuel: { bg: '#f1f5f9', color: '#475569' }
};

// Indicateurs applicables selon le profil de l'organisme (CFA / certifiant).
// Le dénominateur du score en découle automatiquement (23 pour un OF simple, 32 pour
// un CFA certifiant).
export const indicateursApplicables = ({ isCfa = false, isCertifiant = false } = {}) =>
  INDICATEURS.filter((i) => {
    if (i.cfa_only && !isCfa) return false;
    if (i.certifiant_only && !isCertifiant) return false;
    return true;
  });

// Score par critère : conformes / total (indicateurs déjà filtrés par profil).
export const scoresParCritere = (indicateurs, evalParId) =>
  CRITERES.map((c) => {
    const list = indicateurs.filter((i) => i.critere === c.id);
    const conformes = list.filter((i) => (evalParId.get(i.id) || 'non_evalue') === 'conforme').length;
    return {
      critere: c.id,
      conformes,
      total: list.length,
      pct: list.length ? Math.round((conformes / list.length) * 100) : 0
    };
  });

// Score global sur la liste déjà filtrée par profil.
export const scoreGlobal = (indicateurs, evalParId) => {
  const total = indicateurs.length;
  const conformes = indicateurs.filter((i) => (evalParId.get(i.id) || 'non_evalue') === 'conforme').length;
  return { conformes, total, pct: total ? Math.round((conformes / total) * 100) : 0 };
};
